// Company boilerplate — the canonical "About Home SeTV®" paragraph.
// Surfaced on the press index, the footer of each release detail page,
// and anywhere else the press kit needs an About blurb. Individual
// releases inherit this by default; override per-release if and when
// the boilerplate evolves and historical releases should retain their
// original text.
export const companyBoilerplate = {
  it:
    "Home SeTV® è una società italiana che sviluppa soluzioni per la comunicazione video immersiva, integrando scenografie virtuali, tecnologia proprietaria e strumenti pensati per professionisti, aziende, formazione e relazioni a distanza. Con il software AVRAH, Home SeTV® inaugura una nuova fase del proprio percorso, orientata a rendere la presenza digitale più efficace, sicura e riconoscibile.",
  en:
    "Home SeTV® is an Italian company developing solutions for immersive video communication, combining virtual sets, proprietary technology, and tools built for professionals, organisations, training, and remote relationships. With the AVRAH software, Home SeTV® enters a new phase of its work, focused on making digital presence more effective, secure, and recognisable.",
};

// Press releases — single source of truth.
//
// Each release is a self-contained document. Add new releases as new
// entries to the `pressReleases` array. The index page renders the list
// reverse-chronologically; the detail page renders one release by slug.
//
// Schema
// ------
//   slug        URL fragment, kebab-case, no date prefix required
//   date        ISO date string (YYYY-MM-DD)
//   location    { it, en }
//   title       { it, en }
//   deck        { it, en } — one-paragraph lede shown under the headline
//   body        { it, en } — ordered list of typed blocks:
//                   { type: "paragraph", text }
//                   { type: "quote", text, attribution }
//   about       { it, en } — company boilerplate shown at the end of
//                            the release. Usually identical across
//                            releases, but kept per-release in case the
//                            boilerplate evolves and historical releases
//                            should retain their original text.

export const pressReleases = [
  {
    slug: "avrah-debutto-banca-mediolanum",
    date: "2026-05-11",
    location: { it: "Torino", en: "Turin" },
    title: {
      it:
        "Home SeTV® presenta A.V.R.A.H. alla Convention di Banca Mediolanum: il debutto del software arriva dopo il Premio Innovazione 2026 alla Camera dei Deputati",
      en:
        "Home SeTV® unveils A.V.R.A.H. at the Banca Mediolanum Convention: the software debuts after the 2026 Innovation Award at the Italian Chamber of Deputies",
    },
    deck: {
      it:
        "Jacopo Mascitelli, ingegnere AI di Stanford, CTO e co-founder di Home SeTV®, ha ricevuto il riconoscimento della Fondazione Italia USA per l'attività innovativa di Home SeTV®. Ora con il nuovo software proprietario AVRAH, l'azienda si prepara al lancio ufficiale durante i lavori di Banca Mediolanum all'Inalpi Arena di Torino.",
      en:
        "Jacopo Mascitelli, a Stanford-trained AI engineer and CTO and co-founder of Home SeTV®, has been recognised by Fondazione Italia USA for the company's innovative work. With its new proprietary software AVRAH, Home SeTV® now prepares for the official launch at Banca Mediolanum's event at the Inalpi Arena in Turin.",
    },
    body: {
      it: [
        {
          type: "paragraph",
          text:
            "Parte sotto il segno dell'innovazione riconosciuta il debutto ufficiale di AVRAH, il nuovo software proprietario di Home SeTV®, che verrà presentato oggi e domani durante la due giorni di lavori di Banca Mediolanum all'Inalpi Arena di Torino.",
        },
        {
          type: "paragraph",
          text:
            "Il lancio arriva a pochi giorni da un importante riconoscimento istituzionale: l'8 maggio, presso la Camera dei Deputati, Jacopo Mascitelli, CTO e co-founder di Home SeTV®, già Under 30 Forbes 2025, ha ricevuto il Premio Innovazione 2026 assegnato dalla Fondazione Italia USA nell'ambito del premio dedicato alle aziende innovative.",
        },
        {
          type: "paragraph",
          text:
            "Un riconoscimento particolarmente significativo perché, come è stato sottolineato durante la cerimonia, la selezione non è avvenuta attraverso autocandidature, ma sulla base di parametri oggettivi individuati dalla Fondazione, a partire dall'analisi di migliaia di imprese italiane.",
        },
        {
          type: "paragraph",
          text:
            "AVRAH nasce con un obiettivo preciso: portare nel mondo del lavoro una nuova qualità della comunicazione digitale. Oltre l'evoluzione della videocall, un ambiente capace di aumentare in modo concreto la presenza percepita di professionisti, aziende e team, rendendo ogni interazione più immersiva, più autorevole e più vicina.",
        },
        {
          type: "paragraph",
          text:
            "Il software, sviluppato in Italia come risposta europea al mercato della videocomunicazione professionale extra-UE, è progettato per operare completamente in locale, senza dipendere da cloud o server esterni fuori dal controllo dell'utilizzatore. Una caratteristica che assume un valore sempre più strategico in un momento in cui innovazione, sicurezza, sostenibilità tecnologica e indipendenza digitale diventano fattori decisivi per la crescita delle imprese e degli studi professionali.",
        },
        {
          type: "quote",
          text:
            "«AVRAH rappresenta un passaggio fondamentale per Home SeTV®. Per anni abbiamo lavorato per trasformare la comunicazione video in qualcosa di più umano, più forte, più memorabile. Oggi questo percorso incontra due segnali importanti: da un lato il riconoscimento ricevuto per l'innovazione, dall'altro il debutto del nostro software davanti a una delle più grandi reti professionali italiane. È un inizio che sentiamo come una responsabilità, non solo come un traguardo.»",
          attribution:
            "Giuseppe Mascitelli · Pioniere del video streaming e Presidente di Home SeTV®",
        },
        {
          type: "paragraph",
          text:
            "Con AVRAH, Home SeTV® si propone di aprire una nuova fase nella comunicazione professionale: più immersiva, più sicura, più identitaria. Una tecnologia pensata non per sostituire la relazione umana, ma per restituirle forza anche quando avviene a distanza.",
        },
        {
          type: "paragraph",
          text:
            "Il riconoscimento della Fondazione Italia USA e il lancio durante la Convention di Banca Mediolanum segnano così due momenti distinti ma profondamente collegati: la conferma istituzionale di un percorso innovativo e il primo passo pubblico di una tecnologia pensata per entrare nel mercato europeo con una visione chiara. Quella di rendere la comunicazione digitale non solo più efficiente, ma finalmente più presente.",
        },
      ],
      en: [
        {
          type: "paragraph",
          text:
            "The official debut of AVRAH, Home SeTV®'s new proprietary software, opens under the banner of recognised innovation. The product is being presented today and tomorrow during the two-day Banca Mediolanum event at the Inalpi Arena in Turin.",
        },
        {
          type: "paragraph",
          text:
            "The launch comes only days after a significant institutional recognition. On 8 May, at the Italian Chamber of Deputies, Jacopo Mascitelli, CTO and co-founder of Home SeTV® and a 2025 Forbes 30 Under 30 honoree, received the 2026 Innovation Award from Fondazione Italia USA, presented as part of its programme for innovative companies.",
        },
        {
          type: "paragraph",
          text:
            "The recognition carries particular weight because, as noted at the ceremony, the selection was not made through self-nomination but on the basis of objective criteria defined by the foundation, drawn from an analysis of thousands of Italian companies.",
        },
        {
          type: "paragraph",
          text:
            "AVRAH was built with a clear goal: to bring a new standard of digital communication into professional work. Not simply an evolution of the video call, but an environment designed to substantively raise the perceived presence of professionals, companies, and teams, making every interaction more immersive, more authoritative, and closer.",
        },
        {
          type: "paragraph",
          text:
            "Developed in Italy as a European answer to the non-EU professional video communication market, the software is designed to run entirely on the user's machine, without relying on cloud services or external servers outside the user's control. That design choice is becoming strategically important at a time when innovation, security, technological sustainability, and digital independence are decisive factors for the growth of companies and professional firms.",
        },
        {
          type: "quote",
          text:
            "\"AVRAH represents a turning point for Home SeTV®. For years we have worked to turn video communication into something more human, stronger, more memorable. Today this journey meets two important signals: on one side the recognition received for innovation, on the other the debut of our software in front of one of the largest professional networks in Italy. It is a beginning we feel as a responsibility, not only as a milestone.\"",
          attribution:
            "Giuseppe Mascitelli · Video streaming pioneer and President of Home SeTV®",
        },
        {
          type: "paragraph",
          text:
            "With AVRAH, Home SeTV® sets out to open a new chapter in professional communication: more immersive, more secure, more recognisably its own. A technology designed not to replace human relationships, but to restore their full weight even when they happen at a distance.",
        },
        {
          type: "paragraph",
          text:
            "The recognition from Fondazione Italia USA and the launch at the Banca Mediolanum Convention mark two distinct but deeply connected moments: institutional confirmation of an innovative path, and the first public step of a technology built to enter the European market with a clear vision. To make digital communication not only more efficient, but at last more present.",
        },
      ],
    },
    about: companyBoilerplate,
  },
];

// Map an i18n.language code to the language key used inside each release.
// Defaults to English when the active language isn't Italian, since the
// site's two real surfaces are it-IT and en-*.
export const releaseLang = (i18nLanguage) =>
  i18nLanguage?.toLowerCase().startsWith("it") ? "it" : "en";

// Sorted reverse-chronologically — the list view consumes this directly.
export const sortedReleases = () =>
  [...pressReleases].sort((a, b) => b.date.localeCompare(a.date));

export const getReleaseBySlug = (slug) =>
  pressReleases.find((r) => r.slug === slug) || null;

// Format an ISO date into a locale-natural string ("11 maggio 2026" or
// "11 May 2026"). Used in the dateline eyebrow and the index list.
export const formatReleaseDate = (isoDate, i18nLanguage) => {
  const date = new Date(isoDate);
  if (Number.isNaN(date.getTime())) return isoDate;
  const locale = releaseLang(i18nLanguage) === "it" ? "it-IT" : "en-GB";
  return new Intl.DateTimeFormat(locale, {
    day: "numeric",
    month: "long",
    year: "numeric",
  }).format(date);
};
